import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  PaperAirplaneIcon as SendIcon,
  XMarkIcon as CloseIcon,
  UserIcon as PersonIcon,
  CpuChipIcon as BotIcon,
  StopIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon as CopyIcon,
  ArrowPathIcon as RefreshIcon,
  LightBulbIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon as ChatIconSolid } from '@heroicons/react/24/solid';
import { v4 as uuidv4 } from 'uuid';
import GraphqlService from '../../service/graphqlService';
import { useProject } from '../../config/ProjectContext';
import { useSnackbar } from '../../providers/SnackbarContext';
import ChatHelpingQuestionsModal from './components/ChatHelpingQuestionsModal';
import { saveChatHistoryItem } from './services/chatHistoryService';
import useAnalytics from '../../hooks/useAnalytics';
import { convertMarkdownToHtml } from './utils/markdownToHtml';

// Custom Confirmation Dialog Component (Tailwind + Heroicons)
function TeamsShareConfirmDialog({ open, onClose, onConfirm }) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000]" onClick={onClose} />
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
              <ShareIcon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Share to Microsoft Teams</h3>
          </div>

          <div className="mb-6 space-y-3">
            <p className="text-gray-700 font-semibold flex items-center gap-2">
              <span className="text-2xl">✅</span>
              <span>FULL MESSAGE COPIED TO CLIPBOARD!</span>
            </p>
            <p className="text-gray-600 text-sm">
              📋 The complete Q&A has been copied to your clipboard.
            </p>

            <div className="bg-purple-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-purple-900 mb-2">📌 Next steps:</p>
              <ol className="text-sm text-purple-800 space-y-1 list-decimal list-inside">
                <li>Click &quot;Open Teams&quot; to open Microsoft Teams</li>
                <li>Select a chat or channel in Teams</li>
                <li>Paste the message (Ctrl+V or Cmd+V)</li>
                <li>Send!</li>
              </ol>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-800 transition-all shadow-lg hover:shadow-xl"
            >
              Open Teams
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function RubiqAgentChat() {
  const [open, setOpen] = useState(false);
  const { project } = useProject();
  const openSnackBar = useSnackbar();
  const { setAnalytics } = useAnalytics();
  const [showTeamsDialog, setShowTeamsDialog] = useState(false);

  // Initialize conversations with a default conversation
  const initialConversationId = uuidv4();
  const [conversations, setConversations] = useState({
    [initialConversationId]: {
      id: initialConversationId,
      messages: [],
      title: 'New conversation',
      unreadCount: 0,
      lastActivity: new Date(),
      loading: false,
      currentSocketId: null,
      serialNumber: null
    }
  });

  const [activeConversationId, setActiveConversationId] = useState(initialConversationId);
  const [input, setInput] = useState('');
  const [showHelpingQuestionsModal, setShowHelpingQuestionsModal] = useState(false);
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const knowledgeUpdateRef = useRef(new Set());

  // Router hooks for auto-detecting serial number from URL
  const location = useLocation();
  const params = useParams();

  // Get current conversation data
  const currentConversation = conversations[activeConversationId];
  const messages = currentConversation?.messages || [];
  const unreadCount = Object.values(conversations).reduce((total, conv) => total + conv.unreadCount, 0);
  const loading = currentConversation?.loading || false;
  const currentSocketId = currentConversation?.currentSocketId || null;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Scroll to bottom when messages change or when loading state changes
  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, loading, open, scrollToBottom]);

  // Simple unread count management
  useEffect(() => {
    if (open) {
      setConversations(prev => ({
        ...prev,
        [activeConversationId]: {
          ...prev[activeConversationId],
          unreadCount: 0
        }
      }));
    }
  }, [open, activeConversationId]);

  // Auto-update knowledge when opening chat with a device
  useEffect(() => {
    if (open && currentConversation?.serialNumber && !knowledgeUpdateRef.current.has(activeConversationId)) {
      knowledgeUpdateRef.current.add(activeConversationId);

      GraphqlService.fetchDeviceKnowledge(String(project?.code || '1'), currentConversation.serialNumber)
        .then(() => {
          if (open) {
            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                messages: [
                  ...prev[activeConversationId].messages,
                  {
                    id: uuidv4(),
                    type: 'system',
                    content: `Knowledge updated (${currentConversation.serialNumber})`,
                    timestamp: new Date(),
                    read: true,
                    isSystem: true,
                  }
                ],
                lastActivity: new Date()
              }
            }));
          }
        })
        .catch(err => {
          console.error('❌ [RubiqChat] Knowledge update failed:', err);
          if (open) {
            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                messages: [
                  ...prev[activeConversationId].messages,
                  {
                    id: uuidv4(),
                    type: 'system',
                    content: `⚠️ Failed to update knowledge for ${currentConversation.serialNumber}`,
                    timestamp: new Date(),
                    read: true,
                    isSystem: true,
                  }
                ],
                lastActivity: new Date()
              }
            }));
          }
        });
    }
  }, [open, activeConversationId, currentConversation?.serialNumber, project?.code]);

  // Auto-detect serial number from URL when on deviceOverview route
  useEffect(() => {

    const isOnDeviceOverview = location.pathname.startsWith('/statistics/deviceOverview/') ||
      (location.hash && location.hash.includes('/statistics/deviceOverview/'));

    if (isOnDeviceOverview) {
      let deviceSerial = params.deviceSerial;

      if (!deviceSerial) {
        let pathToCheck = location.pathname;
        if (location.hash && location.hash.startsWith('#/')) {
          pathToCheck = location.hash.substring(1);
        }

        const pathParts = pathToCheck.split('/');
        // Route format: /statistics/deviceOverview/:projectCode/:deviceSerial
        // pathParts: ['', 'statistics', 'deviceOverview', projectCode, deviceSerial]
        if (pathParts.length >= 5 && pathParts[1] === 'statistics' && pathParts[2] === 'deviceOverview') {
          deviceSerial = decodeURIComponent(pathParts[4]); // Take the 5th part (deviceSerial)
        }
      }

      if (deviceSerial && deviceSerial !== currentConversation?.serialNumber) {
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            serialNumber: deviceSerial
          }
        }));
      }
    } else {
      if (currentConversation?.serialNumber) {
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            serialNumber: null
          }
        }));
      }
    }
  }, [location.pathname, location.hash, params.deviceSerial, activeConversationId, currentConversation?.serialNumber]);

  const createNewConversation = useCallback(() => {
    const newConversationId = uuidv4();

    let autoDetectedSerial = null;
    const isOnDeviceOverview = location.pathname.startsWith('/statistics/deviceOverview/') ||
      (location.hash && location.hash.includes('/statistics/deviceOverview/'));

    if (isOnDeviceOverview) {
      autoDetectedSerial = params.deviceSerial;

      if (!autoDetectedSerial) {
        let pathToCheck = location.pathname;
        if (location.hash && location.hash.startsWith('#/')) {
          pathToCheck = location.hash.substring(1);
        }

        const pathParts = pathToCheck.split('/');
        // Route format: /statistics/deviceOverview/:projectCode/:deviceSerial
        // pathParts: ['', 'statistics', 'deviceOverview', projectCode, deviceSerial]
        if (pathParts.length >= 5 && pathParts[1] === 'statistics' && pathParts[2] === 'deviceOverview') {
          autoDetectedSerial = decodeURIComponent(pathParts[4]); // Take the 5th part (deviceSerial)
        }
      }
    }

    const newConversation = {
      id: newConversationId,
      messages: [],
      title: 'New conversation',
      unreadCount: 0,
      lastActivity: new Date(),
      loading: false,
      currentSocketId: null,
      serialNumber: autoDetectedSerial
    };

    setConversations(prev => ({
      ...prev,
      [newConversationId]: newConversation
    }));

    setActiveConversationId(newConversationId);
  }, [location.pathname, location.hash, params.deviceSerial]);

  const switchConversation = useCallback((conversationId) => {
    setActiveConversationId(conversationId);
    setConversations(prev => ({
      ...prev,
      [conversationId]: {
        ...prev[conversationId],
        unreadCount: 0
      }
    }));
  }, []);

  const updateConversationTitle = useCallback((conversationId, title) => {
    setConversations(prev => ({
      ...prev,
      [conversationId]: {
        ...prev[conversationId],
        title: title.slice(0, 30) + (title.length > 30 ? '...' : '')
      }
    }));
  }, []);

  const deleteConversation = useCallback((conversationId, event) => {
    event?.stopPropagation();

    const conversationIds = Object.keys(conversations);
    if (conversationIds.length <= 1) {
      return;
    }

    if (conversationId === activeConversationId) {
      const remainingConversations = conversationIds.filter(id => id !== conversationId);
      if (remainingConversations.length > 0) {
        setActiveConversationId(remainingConversations[0]);
      }
    }

    setConversations(prev => {
      const newConversations = { ...prev };
      delete newConversations[conversationId];
      return newConversations;
    });
  }, [conversations, activeConversationId]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setInput(suggestion);
  }, []);

  const saveToHistory = useCallback((question, answer, conversationId) => {
    try {
      const conversation = conversations[conversationId];
      const lastUserMessage = [...conversation.messages]
        .reverse()
        .find(msg => msg.type === 'user');

      // Auto-categorize based on question content
      const categorizQuestion = (question) => {
        const q = question.toLowerCase();

        if (q.includes('firmware') || q.includes('update') || q.includes('upgrade') || q.includes('version')) {
          return 'firmware';
        }
        if (q.includes('connect') || q.includes('disconnect') || q.includes('online') || q.includes('offline') || q.includes('network')) {
          return 'connection';
        }
        if (q.includes('error') || q.includes('problem') || q.includes('issue') || q.includes('fail') || q.includes('wrong')) {
          return 'error';
        }
        if (q.includes('consumption') || q.includes('usage') || q.includes('energy') || q.includes('power')) {
          return 'consumption';
        }
        if (q.includes('recipe') || q.includes('program') || q.includes('cycle')) {
          return 'recipe';
        }
        if (q.includes('history') || q.includes('log') || q.includes('past') || q.includes('previous')) {
          return 'history';
        }
        if (q.includes('status') || q.includes('state') || q.includes('condition')) {
          return 'info';
        }
        if (q.includes('gateway') || q.includes('hub')) {
          return 'gateway';
        }

        return 'general';
      };

      saveChatHistoryItem({
        question: question,
        answer: answer,
        deviceSerial: lastUserMessage?.deviceSerial || currentConversation?.serialNumber || null,
        category: categorizQuestion(question),
        conversationId: conversationId,
      });
    } catch (error) {
      console.error('Failed to save to history:', error);
    }
  }, [conversations, currentConversation?.serialNumber]);

  const formatTimestamp = useCallback((timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    if (diffDays === 1) {
      return `Yesterday ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }

    if (diffDays < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${dayName} ${time}`;
    }

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      openSnackBar('✅ Message copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy text: ', err);
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        openSnackBar('✅ Message copied to clipboard!', 'success');
      } catch (e) {
        openSnackBar('❌ Failed to copy message', 'error');
      }
    }
  }, [openSnackBar]);

  const shareToTeams = useCallback((question, answer) => {
    const cleanAnswer = answer
      .replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, ''))
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '');

    const deviceInfo = currentConversation?.serialNumber
      ? `Device: ${currentConversation.serialNumber}\n\n`
      : '';

    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const message = `📋 IOT-A Chat - Q&A\n\n` +
      `⏰ ${timestamp}\n` +
      `${deviceInfo}` +
      `❓ Question:\n${question}\n\n` +
      `✅ Answer:\n${cleanAnswer}`;

    navigator.clipboard.writeText(message).then(() => {
      setShowTeamsDialog(true);
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      openSnackBar('❌ Failed to copy message to clipboard', 'error');
    });
  }, [currentConversation?.serialNumber, openSnackBar]);

  const handleTeamsDialogConfirm = () => {
    setAnalytics({ event: 'button_clicked', extraParams: { name: 'iota-faq-history-conversation-share-teams' } });
    window.open('https://teams.microsoft.com/', '_blank');
    setShowTeamsDialog(false);
  };

  const setupSubscription = useCallback((socketId) => {
    try {
      const subscription = GraphqlService.createRubiqAgentSubscription(
        socketId,
        (data) => {
          if (data.__typename === 'PublishRubiqAgentResponseSuccess') {

            const responseContent = data.result?.processedResponse || data.result?.notTranslatedResponse || data.result?.rawResponse || 'No response content available';

            const conversation = conversations[activeConversationId];
            const lastUserMessage = [...conversation.messages]
              .reverse()
              .find(msg => msg.type === 'user');

            if (lastUserMessage) {
              saveToHistory(lastUserMessage.content, responseContent, activeConversationId);
            }

            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                messages: [
                  ...prev[activeConversationId].messages,
                  {
                    id: uuidv4(),
                    type: 'bot',
                    content: responseContent,
                    timestamp: new Date(),
                    read: false,
                    followUpQuestions: data.result?.followUpQuestions || [],
                  }
                ],
                lastActivity: new Date()
              }
            }));

            if (!open) {
              setConversations(prev => ({
                ...prev,
                [activeConversationId]: {
                  ...prev[activeConversationId],
                  unreadCount: prev[activeConversationId].unreadCount + 1
                }
              }));
            }

            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                loading: false,
                currentSocketId: null
              }
            }));
          } else if (data.__typename === 'BadRequest' || data.__typename === 'InternalServerError') {
            console.error('❌ [RubiqChat] Received server error:', data);
            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                messages: [
                  ...prev[activeConversationId].messages,
                  {
                    id: uuidv4(),
                    type: 'bot',
                    content: `Server Error: ${data.message || 'Something went wrong'}`,
                    timestamp: new Date(),
                    read: false,
                    isError: true,
                  }
                ],
                lastActivity: new Date()
              }
            }));

            if (!open) {
              setConversations(prev => ({
                ...prev,
                [activeConversationId]: {
                  ...prev[activeConversationId],
                  unreadCount: prev[activeConversationId].unreadCount + 1
                }
              }));
            }

            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                loading: false,
                currentSocketId: null
              }
            }));
          } else {
            console.warn('⚠️ [RubiqChat] Received unexpected response format:', data);
            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                messages: [
                  ...prev[activeConversationId].messages,
                  {
                    id: uuidv4(),
                    type: 'bot',
                    content: 'Received unexpected response format',
                    timestamp: new Date(),
                    read: false,
                    isError: true,
                  }
                ],
                lastActivity: new Date()
              }
            }));

            if (!open) {
              setConversations(prev => ({
                ...prev,
                [activeConversationId]: {
                  ...prev[activeConversationId],
                  unreadCount: prev[activeConversationId].unreadCount + 1
                }
              }));
            }
          }

          setConversations(prev => ({
            ...prev,
            [activeConversationId]: {
              ...prev[activeConversationId],
              loading: false,
              currentSocketId: null
            }
          }));
        },
        (error) => {
          console.error('💥 [RubiqChat] Subscription error occurred:', error);

          setConversations(prev => ({
            ...prev,
            [activeConversationId]: {
              ...prev[activeConversationId],
              messages: [
                ...prev[activeConversationId].messages,
                {
                  id: uuidv4(),
                  type: 'bot',
                  content: `Connection Error: ${error.message || 'Failed to establish subscription'}. Please try again.`,
                  timestamp: new Date(),
                  read: false,
                  isError: true
                }
              ],
              lastActivity: new Date()
            }
          }));

          if (!open) {
            setConversations(prev => ({
              ...prev,
              [activeConversationId]: {
                ...prev[activeConversationId],
                unreadCount: prev[activeConversationId].unreadCount + 1
              }
            }));
          }

          setConversations(prev => ({
            ...prev,
            [activeConversationId]: {
              ...prev[activeConversationId],
              loading: false,
              currentSocketId: null
            }
          }));
        }
      );

      if (subscription) {
        subscriptionRef.current = subscription;
      } else {
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            messages: [
              ...prev[activeConversationId].messages,
              {
                id: uuidv4(),
                type: 'bot',
                content: 'Failed to establish connection. Please try again.',
                timestamp: new Date(),
                read: false,
                isError: true
              }
            ],
            lastActivity: new Date()
          }
        }));

        if (!open) {
          setConversations(prev => ({
            ...prev,
            [activeConversationId]: {
              ...prev[activeConversationId],
              unreadCount: prev[activeConversationId].unreadCount + 1
            }
          }));
        }

        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            loading: false
          }
        }));
      }

      return subscription;
    } catch (error) {
      console.error('🚨 [RubiqChat] Exception caught while setting up subscription:', error);
      setConversations(prev => ({
        ...prev,
        [activeConversationId]: {
          ...prev[activeConversationId],
          messages: [
            ...prev[activeConversationId].messages,
            {
              id: uuidv4(),
              type: 'bot',
              content: `Setup Error: ${error.message || 'Failed to setup subscription'}. Please try again.`,
              timestamp: new Date(),
              read: false,
              isError: true
            }
          ],
          lastActivity: new Date()
        }
      }));

      if (!open) {
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            unreadCount: prev[activeConversationId].unreadCount + 1
          }
        }));
      }

      setConversations(prev => ({
        ...prev,
        [activeConversationId]: {
          ...prev[activeConversationId],
          loading: false
        }
      }));
    }
  }, [open, activeConversationId, conversations, saveToHistory]);

  const cancelRequest = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    setConversations(prev => ({
      ...prev,
      [activeConversationId]: {
        ...prev[activeConversationId],
        messages: [
          ...prev[activeConversationId].messages,
          {
            id: uuidv4(),
            type: 'bot',
            content: '🛑 Request cancelled by user.',
            timestamp: new Date(),
            read: false,
            isError: false
          }
        ],
        lastActivity: new Date(),
        loading: false,
        currentSocketId: null
      }
    }));

    if (!open) {
      setConversations(prev => ({
        ...prev,
        [activeConversationId]: {
          ...prev[activeConversationId],
          unreadCount: prev[activeConversationId].unreadCount + 1
        }
      }));
    }
  }, [open, activeConversationId, currentSocketId]);

  const updateKnowledgeManually = useCallback(() => {
    if (!currentConversation?.serialNumber) {
      openSnackBar('⚠️ No device selected', 'warning');
      return;
    }

    setConversations(prev => ({
      ...prev,
      [activeConversationId]: {
        ...prev[activeConversationId],
        messages: [
          ...prev[activeConversationId].messages,
          {
            id: uuidv4(),
            type: 'system',
            content: `Updating knowledge for ${currentConversation.serialNumber}...`,
            timestamp: new Date(),
            read: false,
            isSystem: true,
            isLoading: true
          }
        ],
        lastActivity: new Date()
      }
    }));

    GraphqlService.fetchDeviceKnowledge(String(project?.code || '1'), currentConversation.serialNumber)
      .then(() => {
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            messages: prev[activeConversationId].messages
              .filter(msg => !msg.isLoading)
              .concat({
                id: uuidv4(),
                type: 'system',
                content: `Knowledge updated (${currentConversation.serialNumber})`,
                timestamp: new Date(),
                read: true,
                isSystem: true,
              }),
            lastActivity: new Date()
          }
        }));
        openSnackBar('✅ Knowledge updated successfully', 'success');
      })
      .catch(err => {
        console.error('❌ [RubiqChat] Manual knowledge update failed:', err);
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            messages: prev[activeConversationId].messages
              .filter(msg => !msg.isLoading)
              .concat({
                id: uuidv4(),
                type: 'system',
                content: `⚠️ Failed to update knowledge for ${currentConversation.serialNumber}`,
                timestamp: new Date(),
                read: true,
                isSystem: true,
              }),
            lastActivity: new Date()
          }
        }));
        openSnackBar('❌ Failed to update knowledge', 'error');
      });
  }, [activeConversationId, currentConversation?.serialNumber, project?.code, openSnackBar]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    setAnalytics({ event: 'button_clicked', extraParams: { name: 'iota-faq-send_message' } });

    const userMessage = {
      id: uuidv4(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date(),
      read: true,
      deviceSerial: currentConversation?.serialNumber || null
    };

    setConversations(prev => ({
      ...prev,
      [activeConversationId]: {
        ...prev[activeConversationId],
        messages: [...prev[activeConversationId].messages, userMessage],
        lastActivity: new Date()
      }
    }));

    if (currentConversation.messages.length === 0) {
      updateConversationTitle(activeConversationId, userMessage.content);
    }

    setInput('');

    const socketId = uuidv4();

    setConversations(prev => ({
      ...prev,
      [activeConversationId]: {
        ...prev[activeConversationId],
        loading: true,
        currentSocketId: socketId
      }
    }));

    try {
      const projectId = project?.code || 1;
      const queryWithPrefix = `@Portal ${input.trim()}`;
      const routeId = currentConversation?.serialNumber || 'default';
      const routeName = 'device';
      const useStreaming = false;

      const response = await GraphqlService.triggerRubiqAgent({
        projectId,
        conversationId: activeConversationId,
        query: queryWithPrefix,
        socketId,
        routeId,
        routeName,
        useStreaming
      });

      if (response.isDirectResponse && response.result?.processedResponse) {
        saveToHistory(input.trim(), response.result.processedResponse, activeConversationId);

        const botMessage = {
          id: uuidv4(),
          type: 'bot',
          content: response.result.processedResponse,
          timestamp: new Date(),
          read: false,
          isError: false,
          followUpQuestions: response.result.followUpQuestions || [],
          metadata: {
            agentId: response.result.agentId,
            action: response.result.action,
            status: response.result.status,
            rawResponse: response.result.rawResponse,
            notTranslatedResponse: response.result.notTranslatedResponse,
            referenceList: response.result.referenceList,
            notTranslatedReferenceList: response.result.notTranslatedReferenceList,
            actionDefinition: response.result.actionDefinition,
            conversationStarters: response.result.conversationStarters,
            followUpQuestions: response.result.followUpQuestions
          }
        }; setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            messages: [...prev[activeConversationId].messages, botMessage],
            lastActivity: new Date(),
            loading: false,
            currentSocketId: null
          }
        }));

        if (!open) {
          setConversations(prev => ({
            ...prev,
            [activeConversationId]: {
              ...prev[activeConversationId],
              unreadCount: prev[activeConversationId].unreadCount + 1
            }
          }));
        }
      } else if (useStreaming) {
        setupSubscription(socketId);
      }
    } catch (error) {
      console.error('❌ [RubiqChat] Error sending message:', error);
      setConversations(prev => ({
        ...prev,
        [activeConversationId]: {
          ...prev[activeConversationId],
          messages: [
            ...prev[activeConversationId].messages,
            { id: uuidv4(), type: 'bot', content: `Error: ${error.message || 'Failed to send message. Please try again.'}`, timestamp: new Date(), read: false, isError: true }
          ],
          lastActivity: new Date(),
          loading: false,
          currentSocketId: null
        }
      }));

      if (!open) {
        setConversations(prev => ({
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            unreadCount: prev[activeConversationId].unreadCount + 1
          }
        }));
      }

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
  };

  // Restrict chatbot to Vertuo (28), Momento (5), brand 22, and KOTO (39) projects only
  const allowedProjects = [5, 22, 28, 39, '5', '22', '28', '39'];
  const isProjectAllowed = allowedProjects.includes(project?.code);

  // Don't render anything if project is not allowed
  if (!isProjectAllowed) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => {
          setAnalytics({ event: 'section_loaded', sectionId: 'iota' });
          setOpen(true);
        }}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-[9999] bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 transition-all duration-200 hover:scale-110"
        aria-label="open chat"
      >
        <div className="relative">
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
          <ChatIconSolid className="h-6 w-6 text-white" />
        </div>
      </button>

      {/* Drawer Overlay + Drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={handleClose}
          />
          <div className="fixed right-0 top-0 h-full w-[600px] bg-gradient-to-br from-purple-500 to-purple-700 text-white shadow-2xl z-[9999] flex flex-col">

            {/* Header */}
            <div className="p-4 bg-black bg-opacity-10 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <BotIcon className="h-6 w-6" />
                <h2 className="text-xl font-semibold">IOT-A</h2>
              </div>
              <button
                onClick={handleClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
              >
                <CloseIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Conversation Tabs */}
            <div className="p-2 bg-black bg-opacity-5 border-b border-white border-opacity-20 flex-shrink-0">
                <div className="flex items-center gap-1 overflow-x-auto">
                {Object.values(conversations)
                  .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
                  .map(conversation => (
                    <div
                      key={conversation.id}
                      className={`flex items-center rounded-2xl border-2 relative min-w-[100px] ${conversation.id === activeConversationId
                        ? 'bg-white bg-opacity-20 border-white border-opacity-40'
                        : 'bg-white bg-opacity-10 border-transparent'
                        }`}
                    >
                      <button
                        onClick={() => switchConversation(conversation.id)}
                        className="px-3 py-2 flex-1 text-center text-xs"
                      >
                        {conversation.title} {conversation.loading && '⏳'}
                      </button>

                      {Object.keys(conversations).length > 1 && (
                        <button
                          onClick={(e) => deleteConversation(conversation.id, e)}
                          className="text-white text-opacity-70 hover:text-opacity-100 p-1 mr-1"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      )}

                      {conversation.unreadCount > 0 && conversation.id !== activeConversationId && (
                        <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          {conversation.unreadCount}
                        </div>
                      )}
                    </div>
                  ))}
                <button
                  onClick={createNewConversation}
                  className="text-white min-w-[40px] hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-2">
                {messages.map((message) => {
                  // System messages
                  if (message.type === 'system' || message.isSystem) {
                    return (
                      <div
                        key={message.id}
                        className="flex justify-center items-center py-3 px-2 mb-2"
                      >
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold shadow-md border border-white border-opacity-30 ${message.isLoading
                            ? 'bg-blue-500 bg-opacity-90'
                            : message.content.includes('Failed') || message.content.includes('⚠️')
                              ? 'bg-red-500 bg-opacity-90'
                              : 'bg-green-500 bg-opacity-90'
                            }`}
                        >
                          {message.isLoading && (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          )}
                          {!message.isLoading && (
                            <span className="text-base">
                              {message.content.includes('Failed') || message.content.includes('⚠️') ? '⚠️' : '✅'}
                            </span>
                          )}
                          <span>{message.content}</span>
                        </div>
                      </div>
                    );
                  }

                  // User/Bot messages
                  return (
                    <div
                      key={message.id}
                      className={`p-2 mb-2 flex items-start gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'
                        }`}
                    >
                      {message.type === 'bot' && (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white bg-opacity-20 flex-shrink-0">
                          <BotIcon className="h-4 w-4" />
                        </div>
                      )}

                      <div className="max-w-[90%]">
                        <div
                          className={`p-4 rounded-2xl shadow-lg ${message.type === 'user'
                            ? 'bg-white bg-opacity-20 text-white'
                            : 'bg-white bg-opacity-95 text-gray-800'
                            }`}
                        >
                          {message.type === 'user' && message.deviceSerial && (
                            <div className="mb-2">
                              <span className="inline-block bg-green-500 bg-opacity-80 text-white text-xs font-bold px-2 py-1 rounded-full">
                                📱 {message.deviceSerial}
                              </span>
                            </div>
                          )}

                          <div className="prose prose-sm max-w-none break-words">
                            <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(message.content) }} />
                          </div>

                          <div
                            className={`mt-2 text-xs italic ${message.type === 'user' ? 'text-right text-white text-opacity-70' : 'text-left text-gray-500'
                              }`}
                          >
                            {formatTimestamp(message.timestamp)}
                          </div>
                        </div>

                        {/* Follow-up questions */}
                        {message.type === 'bot' && message.followUpQuestions && message.followUpQuestions.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs font-semibold text-white text-opacity-80 flex items-center gap-1">
                              <LightBulbIcon className="h-4 w-4" />
                              Follow-up questions:
                            </p>
                            <div className="flex flex-col gap-2">
                              {message.followUpQuestions.map((question, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    setInput(question);
                                  }}
                                  disabled={loading}
                                  className="bg-white bg-opacity-15 hover:bg-opacity-25 text-white text-left text-sm px-3 py-2 rounded-lg border border-white border-opacity-20 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="mr-2">💬</span>
                                  {question}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action buttons for bot messages */}
                        {message.type === 'bot' && (
                          <div className="flex justify-end gap-2 mt-2 mr-1">
                            <button
                              onClick={() => copyToClipboard(message.content)}
                              className="bg-blue-500 text-white p-2 rounded-lg shadow-md hover:bg-blue-600 hover:scale-110 transition-all w-8 h-8 flex items-center justify-center"
                              title="Copy message"
                            >
                              <CopyIcon className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => {
                                const messageIndex = messages.indexOf(message);
                                const previousMessages = messages.slice(0, messageIndex).reverse();
                                const userQuestion = previousMessages.find(m => m.type === 'user');
                                const question = userQuestion?.content || 'Question not found';
                                shareToTeams(question, message.content);
                              }}
                              className="bg-purple-600 text-white p-2 rounded-lg shadow-md hover:bg-purple-700 hover:scale-110 transition-all w-8 h-8 flex items-center justify-center"
                              title="Share to Microsoft Teams"
                            >
                              <ShareIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {message.type === 'user' && (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white bg-opacity-20 flex-shrink-0">
                          <PersonIcon className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Helping Questions Button */}
                {(() => {
                  // Get the current path (check both pathname and hash)
                  const pathToCheck = location.hash.startsWith('#/')
                    ? location.hash.substring(1)
                    : location.pathname;

                  // Check if we're on deviceOverview route
                  const isOnDeviceOverview = pathToCheck.includes('/deviceOverview');

                  // Check if there's a serial in the path: /deviceOverview/:projectCode/:deviceSerial
                  // We need to check if there are at least 5 parts in the path
                  const pathParts = pathToCheck.split('/');
                  const hasSerialInPath = pathParts.length >= 5 && 
                                         pathParts[1] === 'statistics' && 
                                         pathParts[2] === 'deviceOverview' &&
                                         pathParts[4]; // deviceSerial exists

                  // Show "Select Machine" message if on deviceOverview without serial
                  const shouldShowSelectMachineMessage = isOnDeviceOverview && !hasSerialInPath;

                  if (shouldShowSelectMachineMessage) {
                    return (
                      <div className="p-6 text-center">
                        <div className="bg-white bg-opacity-10 rounded-2xl p-8 border-2 border-white border-opacity-20">
                          <div className="text-6xl mb-4">🔍</div>
                          <h3 className="text-2xl font-bold text-white mb-4">Select a Machine First</h3>
                          <p className="text-white text-lg mb-4">
                            To ask for information about a specific machine, please select it first.
                          </p>
                          <button
                            onClick={() => setShowHelpingQuestionsModal(true)}
                            className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-3xl font-bold shadow-lg border-2 border-white border-opacity-30 hover:bg-opacity-30 hover:scale-105 transition-all flex items-center gap-2 mx-auto"
                          >
                            <LightBulbIcon className="h-5 w-5" />
                            Browse Frequently Asked Questions
                          </button>
                        </div>
                      </div>
                    );
                  }

                  // Show normal empty state message
                  if (messages.length === 0 && !loading) {
                    return (
                      <div className="p-6 text-center">
                        <p className="mb-4 text-white text-base">
                          👋 Hi! How can I help you today?
                        </p>
                        <button
                          onClick={() => setShowHelpingQuestionsModal(true)}
                          className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-3xl font-bold shadow-lg border-2 border-white border-opacity-30 hover:bg-opacity-30 hover:scale-105 transition-all flex items-center gap-2 mx-auto"
                        >
                          <LightBulbIcon className="h-5 w-5" />
                          Browse Frequently Asked Questions
                        </button>
                      </div>
                    );
                  }

                  // Show FAQ button for active conversation
                  if (messages.length > 0 && !loading) {
                    return (
                      <div className="p-2 text-center">
                        <button
                          onClick={() => setShowHelpingQuestionsModal(true)}
                          className="bg-blue-500 bg-opacity-80 text-white px-4 py-2 rounded-2xl text-sm font-medium shadow-md hover:bg-blue-600 hover:scale-105 transition-all flex items-center gap-2 mx-auto"
                        >
                          <LightBulbIcon className="h-4 w-4" />
                          Browse Frequently Asked Questions
                        </button>
                      </div>
                    );
                  }

                  return null;
                })()}

                {loading && (
                  <div className="p-2 text-center">
                    <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-sm">Thinking longer for a better answer...</p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="h-px bg-white bg-opacity-20 flex-shrink-0" />

              {/* Input Section */}
              <div className="p-4 bg-black bg-opacity-10 flex-shrink-0">
                {currentConversation?.serialNumber && (
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="inline-block bg-white bg-opacity-20 text-white text-xs px-3 py-1 rounded-full">
                      Device: {currentConversation.serialNumber}
                    </span>
                    <button
                      onClick={updateKnowledgeManually}
                      disabled={loading}
                      className="bg-green-500 bg-opacity-90 text-white text-xs px-3 py-1 rounded-xl font-bold shadow-md border border-white border-opacity-30 hover:bg-green-600 hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <RefreshIcon className="h-4 w-4" />
                      Update Knowledge
                    </button>
                  </div>
                )}

                <div className="flex gap-2 items-end">
                  <textarea
                    placeholder="Ask me anything about IoT..."
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      // Auto-resize textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                    }}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    rows={2}
                    className="flex-1 bg-white bg-opacity-90 text-gray-800 rounded-lg px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50 min-h-[80px]"
                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                  />
                  <button
                    onClick={loading ? cancelRequest : sendMessage}
                    disabled={!loading && !input.trim()}
                    className="bg-white bg-opacity-20 text-white p-2 rounded-lg hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                  >
                    {loading ? (
                      <StopIcon className="h-5 w-5" />
                    ) : (
                      <SendIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Frequently Asked Questions Modal */}
      <ChatHelpingQuestionsModal
        open={showHelpingQuestionsModal}
        onClose={() => setShowHelpingQuestionsModal(false)}
        onQuestionSelect={handleSuggestionClick}
      />

      {/* Teams Share Confirmation Dialog */}
      <TeamsShareConfirmDialog
        open={showTeamsDialog}
        onClose={() => {
          setAnalytics({ event: 'button_clicked', extraParams: { name: 'iota-faq-history-conversation-share-cancel' } });
          setShowTeamsDialog(false);
        }}
        onConfirm={handleTeamsDialogConfirm}
      />
    </>
  );
}
